// Resize a raster image file to fit within target dimensions while preserving
// aspect ratio. Outputs a PNG so transparency is retained. SVGs are returned
// unchanged since they are already resolution-independent.

export interface ResizeOptions {
  maxWidth?: number;
  maxHeight?: number;
  mimeType?: 'image/png' | 'image/jpeg' | 'image/webp';
  quality?: number;
}

const DEFAULTS: Required<ResizeOptions> = {
  maxWidth: 512,
  maxHeight: 512,
  mimeType: 'image/png',
  quality: 0.92,
};

export async function resizeLogoImage(
  file: File,
  options: ResizeOptions = {}
): Promise<File> {
  // SVGs are vector — no need to resize.
  if (file.type === 'image/svg+xml') return file;

  const opts = { ...DEFAULTS, ...options };

  const bitmap = await loadBitmap(file);
  const { width: srcW, height: srcH } = bitmap;

  // If already smaller than target, still normalize to PNG canvas of natural
  // size so downstream renderers get a consistent format.
  const scale = Math.min(opts.maxWidth / srcW, opts.maxHeight / srcH, 1);
  const targetW = Math.max(1, Math.round(srcW * scale));
  const targetH = Math.max(1, Math.round(srcH * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    if ('close' in bitmap) (bitmap as ImageBitmap).close();
    return file;
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap as CanvasImageSource, 0, 0, targetW, targetH);
  if ('close' in bitmap) (bitmap as ImageBitmap).close();

  const blob: Blob | null = await new Promise((resolve) =>
    canvas.toBlob(resolve, opts.mimeType, opts.quality)
  );
  if (!blob) return file;

  const baseName = file.name.replace(/\.[^.]+$/, '');
  const ext = opts.mimeType === 'image/jpeg' ? 'jpg' : opts.mimeType.split('/')[1];
  return new File([blob], `${baseName}.${ext}`, {
    type: opts.mimeType,
    lastModified: Date.now(),
  });
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      // fall through to HTMLImageElement path
    }
  }
  return await new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}
